import {BrickContext, ActionBrick, registerBrick } from 'olympe';
import Module from 'openaistreammodule';
import {getLogger} from "@olympeio/core";

export default class LoadSTTModel extends ActionBrick {
    static dbVersion = 1
    static dbName    = 'whisper.orionis.cloud';
    static urls    = {
        'tiny':     '/models/ggml-model-whisper-tiny.bin',
        'base':     '/models/ggml-model-whisper-base.bin',
    };

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {string} model
     * @param {function()} forwardEvent
     */
    update($, [model], [forwardEvent]) {

        let sizes = {
            'tiny':     75,
            'base':     142,
        };

        let url     = LoadSTTModel.urls[model];
        let dst     = 'whisper.bin';
        let size_mb = sizes[model];

        this.loadRemote(
            url,
            dst,
            size_mb,
            () => {},
            (fname, buf) => {

                // write to WASM file using FS_createDataFile, if the file exists, delete it
                try {
                    Module.FS_unlink(dst);
                } catch (e) {
                    // ignore
                }
                Module.FS_createDataFile("/", fname, buf, true, true);

                getLogger('LocalOpenAI').info('storeFS: stored model: ' + fname + ' size: ' + buf.length);
                forwardEvent();
            },
            () => {
                getLogger('LocalOpenAI').warn('Cancel triggered');
            },
            (m) => getLogger('LocalOpenAI').info(m));
    }



    /**
     * load remote data
     * - check if the data is already in the IndexedDB
     * - if not, fetch it from the remote URL and store it in the IndexedDB
     *
     * @param url
     * @param dst
     * @param size_mb
     * @param cbProgress
     * @param cbReady
     * @param cbCancel
     * @param cbPrint
     */
    loadRemote(url, dst, size_mb, cbProgress, cbReady, cbCancel, cbPrint) {
        if (!navigator.storage || !navigator.storage.estimate) {
            getLogger('LocalOpenAI').info('loadRemote: navigator.storage.estimate() is not supported');
        } else {
            // query the storage quota and print it
            navigator.storage.estimate().then(function (estimate) {
                getLogger('LocalOpenAI').info('loadRemote: storage quota: ' + estimate.quota + ' bytes');
                getLogger('LocalOpenAI').info('loadRemote: storage usage: ' + estimate.usage + ' bytes');
            });
        }

        // check if the data is already in the IndexedDB
        const openConnection = indexedDB.open(LoadSTTModel.dbName, LoadSTTModel.dbVersion);

        openConnection.onupgradeneeded = (event) => {
            const db = event.target.result;
            console.log('coming here', db)
            if (db.version === 1) {
                db.createObjectStore('models', { autoIncrement: false });
                getLogger('LocalOpenAI').info('loadRemote: created IndexedDB ' + db.name + ' version ' + db.version);
            } else {
                // clear the database
                const os = event.currentTarget.transaction.objectStore('models');
                os.clear();
                getLogger('LocalOpenAI').info('loadRemote: cleared IndexedDB ' + db.name + ' version ' + db.version);
            }
        };

        openConnection.onsuccess = (event) => {
            const db = event.target.result;
            const tx = db.transaction(['models'], 'readonly');
            const os = tx.objectStore('models');
            const rq = os.get(url);

            rq.onsuccess = () => {
                if (rq.result) {
                    getLogger('LocalOpenAI').info('loadRemote: "' + url + '" is already in the IndexedDB');
                    cbReady(dst, rq.result);
                } else {
                    // data is not in the IndexedDB
                    getLogger('LocalOpenAI').info('loadRemote: "' + url + '" is not in the IndexedDB');

                    this.fetchRemote(url, cbProgress, cbPrint).then((data) => {
                        if (data) {
                            // store the data in the IndexedDB
                            const newConnection = indexedDB.open(LoadSTTModel.dbName, LoadSTTModel.dbVersion);
                            newConnection.onsuccess = (event) => {
                                const db = event.target.result;
                                const tx = db.transaction(['models'], 'readwrite');
                                const os = tx.objectStore('models');

                                let rqWrite = null;
                                try {
                                    rqWrite = os.put(data, url);
                                } catch (e) {
                                    cbPrint('loadRemote: failed to store "' + url + '" in the IndexedDB: \n' + e);
                                    cbCancel();
                                    return;
                                }

                                rqWrite.onsuccess = function () {
                                    getLogger('LocalOpenAI').info('loadRemote: "' + url + '" stored in the IndexedDB');
                                    cbReady(dst, data);
                                };

                                rqWrite.onerror = function () {
                                    getLogger('LocalOpenAI').error('loadRemote: failed to store "' + url + '" in the IndexedDB');
                                    cbCancel();
                                };
                            };
                        }
                    });
                }
            };

            rq.onerror = () => {
                getLogger('LocalOpenAI').error('loadRemote: failed to get data from the IndexedDB');
                cbCancel();
            };
        };

        openConnection.onerror = function () {
            getLogger('LocalOpenAI').error('loadRemote: failed to open IndexedDB');
            cbCancel();
        };

        openConnection.onblocked = function () {
            getLogger('LocalOpenAI').error('loadRemote: failed to open IndexedDB: blocked');
            cbCancel();
        };

        openConnection.onabort = function () {
            getLogger('LocalOpenAI').warn('loadRemote: failed to open IndexedDB: abort');
            cbCancel();
        };
    }



    /**
     * Do the call and display progress
     *
     * @param url
     * @param cbProgress
     */
    async fetchRemote(url, cbProgress) {

        const response = await fetch(url,{ method: 'GET', });
        if (!response.ok) {
            getLogger('LocalOpenAI').error('fetchRemote: failed to fetch ' + url);
            return;
        }

        const contentLength = response.headers.get('content-length');
        const total = parseInt(contentLength, 10);
        const reader = response.body.getReader();

        const chunks = [];
        let receivedLength = 0;
        let progressLast = -1;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }

            chunks.push(value);
            receivedLength += value.length;

            if (contentLength) {
                cbProgress(receivedLength/total);

                const progressCur = Math.round((receivedLength / total) * 10);
                if (progressCur !== progressLast) {
                    getLogger('LocalOpenAI').info('fetchRemote: fetching ' + 10*progressCur + '% ...');
                    progressLast = progressCur;
                }
            }
        }

        let position = 0;
        const chunksAll = new Uint8Array(receivedLength);

        for (const chunk of chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
        }

        return chunksAll;
    }
}

registerBrick('0190dabe1dbb89a4df5f', LoadSTTModel);
